import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Connects to data-controller="initial-map"

export default class extends Controller {

    static values = {
        lat: String,
        lng: String,
        addressPresent: { type: Boolean, default: false }
    }
    connect() {
        console.log("[initial_map_controller] connected")
        if (this.map) return // Ne pas réinitialiser la carte si déjà créée
        this.map = L.map(this.element, { zoomControl: false })
        this._hasCentered = false
        this._addressMarker = null
        this._lastUserLatLng = null
        this._handleAddressSelected = (event) => this.addressSelected(event)
        this._handleAddressCleared = () => this.addressCleared()
        this._overlayElement = document.getElementById("new-itinerary-sheet")
        this._handleOverlayToggle = () => this.recenterOnCurrentFocus()
        // Utilisation d'un fond de carte en tons gris (CartoDB Positron)
        L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(this.map)
        this.centerOnPrefill()
        this.addUserLocation();
        document.addEventListener("address:selected", this._handleAddressSelected)
        document.addEventListener("address:cleared", this._handleAddressCleared)
        if (this._overlayElement) {
            this._overlayElement.addEventListener("toggle", this._handleOverlayToggle)
        }
    }

    centerOnPrefill() {
        const lat = this.hasLatValue ? parseFloat(this.latValue) : null
        const lng = this.hasLngValue ? parseFloat(this.lngValue) : null
        if (lat && lng) {
            this.setViewForOverlay([lat, lng], 15)
            this._hasCentered = true
            const icon = L.divIcon({
                html: `<div style="width:16px;height:16px;background:#111;border-radius:50%;border:3px solid #fff;box-shadow:0 0 8px rgba(0,0,0,0.35);"></div>`,
                className: '',
                iconSize: [16, 16],
                iconAnchor: [8, 8]
            })
            this._addressMarker = L.marker([lat, lng], { icon }).addTo(this.map)
        }
    }

    addUserLocation() {
        if (this.addressPresentValue) return;
        if (!navigator.geolocation) return;
        this._userMarker = null;
        this._userCircle = null;
        this._geoWatchId = navigator.geolocation.watchPosition(
            position => {
                const { latitude, longitude, accuracy } = position.coords;
                const latlng = [latitude, longitude];
                this._lastUserLatLng = latlng;
                document.dispatchEvent(
                    new CustomEvent("location:updated", { detail: { lat: latitude, lon: longitude } })
                )
                // Centrer la carte sur la première position détectée
                if (!this._hasCentered && !this.addressPresentValue) {
                    this.setViewForOverlay(latlng, 15);
                    this._hasCentered = true;
                }
                // Créer le marqueur si inexistant, sinon mettre à jour
                if (!this._userMarker) {
                    const userIcon = L.divIcon({
                        html: `<div style="width:18px;height:18px;background:#2196f3;border-radius:50%;border:3px solid #fff;box-shadow:0 0 8px #2196f3;position:relative;"></div>` ,
                        className: '',
                        iconSize: [18, 18],
                        iconAnchor: [9, 9]
                    });
                    this._userMarker = L.marker(latlng, { icon: userIcon }).addTo(this.map);
                } else {
                    this._userMarker.setLatLng(latlng);
                }
                // Créer ou mettre à jour le cercle d'exactitude
                if (!this._userCircle) {
                    this._userCircle = L.circle(latlng, {
                        radius: accuracy,
                        color: '#2196f3',
                        fillColor: '#2196f3',
                        fillOpacity: 0.15,
                        weight: 1
                    }).addTo(this.map);
                } else {
                    this._userCircle.setLatLng(latlng);
                    this._userCircle.setRadius(accuracy);
                }
            },
            error => {
                console.warn('[initial_map_controller] Geolocation error:', error)
            },
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
        );
    }

    disconnect() {
        console.log("[initial_map_controller] disconnected")
        document.removeEventListener("address:selected", this._handleAddressSelected)
        document.removeEventListener("address:cleared", this._handleAddressCleared)
        if (this._overlayElement) {
            this._overlayElement.removeEventListener("toggle", this._handleOverlayToggle)
        }
        if (this.map) {
            if (this._userMarker) {
                this.map.removeLayer(this._userMarker);
            }
            if (this._userCircle) {
                this.map.removeLayer(this._userCircle);
            }
            if (this._geoWatchId) {
                navigator.geolocation.clearWatch(this._geoWatchId);
            }
            this.map.remove()
        }
    }

    addressSelected(event) {
        if (!event?.detail) return
        const { lat, lon } = event.detail
        if (!lat || !lon) return
        const latlng = [lat, lon]
        this.setViewForOverlay(latlng, 15)
        this._hasCentered = true
        if (this._addressMarker) {
            this._addressMarker.setLatLng(latlng)
        } else {
            const icon = L.divIcon({
                html: `<div style="width:16px;height:16px;background:#111;border-radius:50%;border:3px solid #fff;box-shadow:0 0 8px rgba(0,0,0,0.35);"></div>`,
                className: '',
                iconSize: [16, 16],
                iconAnchor: [8, 8]
            })
            this._addressMarker = L.marker(latlng, { icon }).addTo(this.map)
        }
    }

    addressCleared() {
        if (this._addressMarker) {
            this.map.removeLayer(this._addressMarker)
            this._addressMarker = null
        }
        if (this._lastUserLatLng) {
            this.setViewForOverlay(this._lastUserLatLng, 15)
            this._hasCentered = true
        }
    }

    setViewForOverlay(latlng, zoom = 15) {
        const centerLatLng = this.centerLatLngForOverlay(latlng, zoom)
        this.map.setView(centerLatLng, zoom, { animate: false })
    }

    shouldOffsetForOverlay() {
        if (!window.matchMedia("(max-width: 639px)").matches) return false
        return Boolean(document.getElementById("start-tour-form"))
    }

    centerLatLngForOverlay(latlng, zoom = 15) {
        if (!this.shouldOffsetForOverlay()) return L.latLng(latlng[0], latlng[1])
        const size = this.map.getSize()
        if (!size || !size.x || !size.y) return L.latLng(latlng[0], latlng[1])

        const targetY = this.targetMarkerY(size.y)
        const centerPoint = L.point(size.x / 2, size.y / 2)
        const targetPoint = L.point(size.x / 2, targetY)
        const delta = targetPoint.subtract(centerPoint)
        const projectedMarker = this.map.project(L.latLng(latlng[0], latlng[1]), zoom)
        return this.map.unproject(projectedMarker.subtract(delta), zoom)
    }

    targetMarkerY(mapHeight) {
        const sheetHeight = this.openSheetHeight()
        if (sheetHeight <= 0) return Math.round(mapHeight * 0.32)
        const safeBottom = sheetHeight + 24
        const upperZoneCenter = (mapHeight - safeBottom) / 2
        return Math.max(80, Math.round(upperZoneCenter))
    }

    openSheetHeight() {
        if (!this._overlayElement || !this._overlayElement.open) return 0
        const rect = this._overlayElement.getBoundingClientRect()
        return Math.max(0, Math.round(rect.height))
    }

    recenterOnCurrentFocus() {
        if (!this.map) return
        if (!this.shouldOffsetForOverlay()) return
        const focusLatLng = this.currentFocusLatLng()
        if (!focusLatLng) return
        this.setViewForOverlay([ focusLatLng.lat, focusLatLng.lng ], this.map.getZoom())
    }

    currentFocusLatLng() {
        if (this._addressMarker) return this._addressMarker.getLatLng()
        if (this._userMarker) return this._userMarker.getLatLng()
        if (this._lastUserLatLng) return L.latLng(this._lastUserLatLng[0], this._lastUserLatLng[1])
        return null
    }
}
